import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";
import { useContext, useEffect } from "react";

import { AuthzError } from "~/access/authzProblem";
import { SnackbarContext } from "~/utils/Contexts";
import { logout } from "~/utils/Helper";
import { ResponseError } from "~/utils/ResponseError";
import { MessageText } from "~/utils/Types";

// The screen already explains it (the empty-scope screen), so a snackbar
// would only repeat it.
const isSilentError = (error: Error): boolean =>
  error instanceof AuthzError && error.isSilent;

// Re-exported for backwards compatibility — `ResponseError` now lives in its
// own leaf module (see ~/utils/ResponseError) to stay out of the import cycle.
export { ResponseError };

export const useQueryErrorEffect = <T>(
  queryResult:
    | UseQueryResult<T, Error>
    | UseMutationResult<T, Error, AxiosRequestConfig, unknown>,
  {
    showErrorSnackbar = true,
  }: {
    /**
     * Set false when the caller renders the error itself (e.g. inline in a
     * dialog) — the 401 logout still applies, only the snackbar is skipped.
     */
    showErrorSnackbar?: boolean;
  } = {},
) => {
  const snackbar = useContext(SnackbarContext);

  useEffect(() => {
    if (queryResult.error) {
      if (
        queryResult.error instanceof ResponseError &&
        queryResult.error.status === 401
      ) {
        logout();
      } else if (showErrorSnackbar && !isSilentError(queryResult.error)) {
        snackbar.open({
          text:
            queryResult.error instanceof ResponseError
              ? queryResult.error.message || MessageText.NETWORK_ERROR
              : MessageText.NETWORK_ERROR,
          type: "error",
        });
      }
    }
  }, [queryResult.error, snackbar, showErrorSnackbar]);
};

/**
 * Similar to useQueryErrorEffect but handles an array of query results
 * Used by useApiQueries to handle errors from multiple queries
 */
export const useQueriesErrorEffect = <T>(
  queryResults: UseQueryResult<T, Error>[],
) => {
  const snackbar = useContext(SnackbarContext);

  useEffect(() => {
    // Check all query results for errors
    const errorResult = queryResults.find((result) => result.error);

    if (errorResult?.error) {
      if (
        errorResult.error instanceof ResponseError &&
        errorResult.error.status === 401
      ) {
        logout();
      } else if (!isSilentError(errorResult.error)) {
        snackbar.open({
          text:
            errorResult.error instanceof ResponseError
              ? errorResult.error.message || MessageText.NETWORK_ERROR
              : MessageText.NETWORK_ERROR,
          type: "error",
        });
      }
    }
  }, [queryResults, snackbar]);
};
